import React, { FC, useContext, useEffect, useRef, useState } from "react"
import { Card, Descriptions, Divider, Space, Tooltip, Typography } from "antd"
import { ProjectContext } from "../../ProjectWrapper"
import {
  CodeLibelle,
  StructureDetailed,
  UpdateStructureRequest
} from "../../../client/backend-client/generated"
import { ReferenceSelect } from "../../../components/AppMetierSelect/ReferenceSelect"
import { ReferenceCode } from "../../../queries/ReferenceQueries"
import { AppMetierSwitch } from "../../../components/AppMetierSwitch"
import { AppMetierTextInput } from "../../../components/AppMetierTextInput/AppMetierTextInput"
import _ from "lodash"
import { useDebouncedCallback } from "use-debounce"
import {
  getFlushEffectCallback,
  LONG_UPDATE_DEBOUNCE_DEFAULT_DELAY
} from "../../../utils/DebounceUtils"
import { structureToRequest, toStructureRequest, useUpdateStructure } from "../../../queries/StructureQueries"
import {
  AppMetierDateInput,
  SERVER_DATE_FORMAT
} from "../../../components/AppMetierDateInput/AppMetierDateInput"
import dayjs from "dayjs"
import { formatDayjs } from "../../../utils/DayjsUtils"
import { AppMetierTitle } from "../../../components/AppMetierTitle/AppMetierTitle"
import { RequiredFieldMessage } from "../project/ProjectForm"
import { CodeAPEAutoComplete } from "./CodeAPEAutoComplete"
import { AddressComponent } from "./AddressComponent"
import { NumberInputWithSteps } from "../../../components/NumberInputWithSteps/NumberInputWithSteps"
import { Suffixes } from "../../../utils/suffixes"
import { NoteContext } from "../../NotePage"
import { validateField } from "../InfosUtils"
import { InfoCircleOutlined } from "@ant-design/icons"

const { Text } = Typography

const LABEL_STYLE = { width: "30%", minWidth: "10rem" }
const DESCRIPTION_ITEM_CLASSNAME = "structure-description-item"

export const StructureForm: FC<{
  structure: StructureDetailed
}> = ({ structure }) => {
  const { structureId, projectId } = useContext(ProjectContext)
  const { holders } = useContext(NoteContext)

  const existsAccesPlus = holders.some(holder => holder.parcoursAccesPlus)
  const updateStructureMutation = useUpdateStructure(structureId, projectId)

  const refs = useRef({
    initialStructureRequest: structureToRequest(structure)
  })

  const [structureRequest, setStructureRequest] = useState<UpdateStructureRequest>(
    refs.current.initialStructureRequest
  )

  // Le code APE peut être complété par Régis, donc on le cache pendant l'enregistrement pour éviter des conflits avec l'utilisateur
  const [isCodeAPELoading, setIsCodeAPELoading] = useState(false)

  const debouncedOnStructureRequestChange = useDebouncedCallback(() => {
    if (isStructureRequestFilled()) {
      if (structure.codeApe2025 != structureRequest.codeApe2025 || structure.codeApe != structureRequest.codeApe) {
        setIsCodeAPELoading(true)
      }
      updateStructureMutation.mutate(
        toStructureRequest(refs.current.initialStructureRequest, structureRequest),
        {
          onSuccess: res => {
            const req = structureToRequest(res)
            refs.current.initialStructureRequest = req
            setStructureRequest(req) 
            setIsCodeAPELoading(false)
          }
        }
      )
    }
  }, LONG_UPDATE_DEBOUNCE_DEFAULT_DELAY)

  useEffect(debouncedOnStructureRequestChange, [structureRequest])
  useEffect(getFlushEffectCallback(debouncedOnStructureRequestChange), [
    debouncedOnStructureRequestChange
  ])

  const isStructureRequestFilled = () => {
    return (
      !_.isEqual(structureRequest, {}) &&
      !_.isEqual(refs.current.initialStructureRequest, structureRequest) &&
      structureRequest.nom &&
      !isErrorFutureCreationDate()
    )
  }

  const isEssFixed = (structureRequest: UpdateStructureRequest) => {
    const { statutJuridique, statutCooperatif } = structureRequest
    return (
      statutJuridique?.code === "ASSO" ||
      statutCooperatif?.code === "SCIC" ||
      statutCooperatif?.code === "SCOP"
    )
  }

  /**
   * Prend une structureRequest en entrée, force ESS à oui si besoin est
   */
  const handleEss = (structureRequest: UpdateStructureRequest): UpdateStructureRequest => {
    if (isEssFixed(structureRequest)) {
      return { ...structureRequest, ess: true }
    }
    return structureRequest
  }

  const isErrorFutureCreationDate = () => {
    const { dateCreation } = structureRequest

    if (!dateCreation) {
      return false
    } else {
      return dayjs(dateCreation).isAfter(dayjs())
    }
  }

  return (
    <Card>
      <Space direction="vertical" className="full-width-space">
        <AppMetierTitle level={4}>Structure</AppMetierTitle>
        <Descriptions bordered size="middle" column={1}>
          <Descriptions.Item
            labelStyle={LABEL_STYLE}
            label={<Text strong>Dénomination sociale*</Text>}
          >
            <AppMetierTextInput
              maxLength={100}
              inputValue={structureRequest.nom}
              onChange={value => {
                setStructureRequest({ ...structureRequest, nom: value })
              }}
              status={structureRequest.nom ? undefined : "error"}
              name=""
            />
            {!structureRequest.nom && <RequiredFieldMessage />}
          </Descriptions.Item>
          <Descriptions.Item labelStyle={LABEL_STYLE} label={<Text strong>Nom commercial</Text>}>
            <AppMetierTextInput
              maxLength={100}
              inputValue={structureRequest.nomCommercial}
              onChange={value => {
                setStructureRequest({
                  ...structureRequest,
                  nomCommercial: value !== "" ? value : undefined
                })
              }}
              name=""
            />
          </Descriptions.Item>
          <Descriptions.Item labelStyle={LABEL_STYLE} label={<Text strong>Téléphone</Text>}>
            <AppMetierTextInput
              maxLength={10}
              pattern={"[0-9]{10}"}
              inputValue={structureRequest.telephone}
              onChange={value => {
                setStructureRequest({
                  ...structureRequest,
                  telephone: value !== "" ? value : undefined
                })
              }}
              name=""
            />
            {validateField(structureRequest.telephone, "phone")}
          </Descriptions.Item>
          <Descriptions.Item labelStyle={LABEL_STYLE} label={<Text strong>Email</Text>}>
            <AppMetierTextInput
              maxLength={255}
              inputValue={structureRequest.email}
              onChange={value => {
                setStructureRequest({
                  ...structureRequest,
                  email: value !== "" ? value : undefined
                })
              }}
              name=""
            />
            {validateField(structureRequest.email, "email")}
          </Descriptions.Item>
        </Descriptions>
      </Space>
      <Divider />
      <Space direction="vertical" className="full-width-space">
        <AddressComponent
          addressData={structureRequest.adresse}
          labelStyle={LABEL_STYLE}
          showCodeCommune
          setData={value => setStructureRequest({ ...structureRequest, adresse: value })}
          initialAddress={structure.adresse}
        />
      </Space>
      <Divider />
      <Space direction="vertical" className="full-width-space">
        <AppMetierTitle level={4}>Informations légales</AppMetierTitle>
        <Descriptions bordered size="middle" column={1}>
          <Descriptions.Item
            className={DESCRIPTION_ITEM_CLASSNAME}
            labelStyle={LABEL_STYLE}
            label={<Text strong>Date de création</Text>}
          >
            <AppMetierDateInput
              status={isErrorFutureCreationDate() ? "error" : undefined}
              value={
                structureRequest.dateCreation ? dayjs(structureRequest.dateCreation) : undefined
              }
              onChange={value =>
                setStructureRequest({
                  ...structureRequest,
                  dateCreation: value ? formatDayjs(value, SERVER_DATE_FORMAT) : undefined
                })
              }
            />
            {isErrorFutureCreationDate() ? <FutureDateMessage paddingLeft={"10px"} /> : null}
          </Descriptions.Item>
          <Descriptions.Item
            className={DESCRIPTION_ITEM_CLASSNAME}
            labelStyle={LABEL_STYLE}
            label={<Text strong>SIRET{existsAccesPlus ? "*" : ""}</Text>}
          >
            <AppMetierTextInput
              maxLength={14}
              inputValue={structureRequest.siret ?? ""}
              onChange={value => {
                setStructureRequest({ ...structureRequest, siret: value })
              }}
              status={existsAccesPlus && !structureRequest.siret ? "error" : undefined}
            />
            {existsAccesPlus && !structureRequest.siret && <RequiredFieldMessage />}
          </Descriptions.Item>          
          <Descriptions.Item
            className={DESCRIPTION_ITEM_CLASSNAME}
            labelStyle={LABEL_STYLE}
            label={<div><Text strong>Code APE 2025</Text><Tooltip
                  placement="bottom"
                  title="Nomenclature NAF 2025, débute le 1er janvier 2026"
                >
                  <InfoCircleOutlined style={{ fontSize: "1em", paddingLeft: "5px" }} />
                </Tooltip></div>}
          >
            {isCodeAPELoading ? null : <CodeAPEAutoComplete
              codeLibelle={structureRequest.codeApe2025}
              allowClear
              status={existsAccesPlus && !structureRequest.codeApe2025 ? "error" : ""}
              onChange={(value?: CodeLibelle) => {
                setStructureRequest({ ...structureRequest, codeApe2025: value })
              }}
              version={2025}
            />}
            {existsAccesPlus && !structureRequest.codeApe2025 && <RequiredFieldMessage />}
          </Descriptions.Item>
          <Descriptions.Item
            className={DESCRIPTION_ITEM_CLASSNAME}
            labelStyle={LABEL_STYLE}
            label={<Text strong>Secteur d'activité 2025</Text>}
          >
            {structure.secteurActivite2025?.libelle ?? ""}
          </Descriptions.Item>
          <Descriptions.Item
            className={DESCRIPTION_ITEM_CLASSNAME}
            labelStyle={LABEL_STYLE}
            label={<div><Text strong>Code APE 2008{existsAccesPlus ? "*" : ""}</Text><Tooltip
                  placement="bottom"
                  title="Nomenclature NAF Rév.2, finit le 31 décembre 2026"
                >
                  <InfoCircleOutlined style={{ fontSize: "1em", paddingLeft: "5px" }} />
                </Tooltip></div>}
          >
            {isCodeAPELoading ? null : <CodeAPEAutoComplete
              codeLibelle={structureRequest.codeApe}
              allowClear
              status={existsAccesPlus && !structureRequest.codeApe ? "error" : ""}
              onChange={(value?: CodeLibelle) => {
                setStructureRequest({ ...structureRequest, codeApe: value })
              }}
              version={2008}
            />}
            {existsAccesPlus && !structureRequest.codeApe && <RequiredFieldMessage />}
          </Descriptions.Item>
          <Descriptions.Item
            className={DESCRIPTION_ITEM_CLASSNAME}
            labelStyle={LABEL_STYLE}
            label={<Text strong>Finalité sociétale</Text>}
          >
            <ReferenceSelect
              referenceCode={ReferenceCode.FINALITE_SOCIETALE}
              code={structureRequest.finaliteSocietale?.code}
              allowClear
              onChange={(value?: CodeLibelle) => {
                setStructureRequest({ ...structureRequest, finaliteSocietale: value })
              }}
            />
          </Descriptions.Item>
          <Descriptions.Item
            className={DESCRIPTION_ITEM_CLASSNAME}
            labelStyle={LABEL_STYLE}
            label={<Text strong>Statut juridique</Text>}
          >
            <ReferenceSelect
              referenceCode={ReferenceCode.STATUT_JURIDIQUE}
              code={structureRequest.statutJuridique?.code}
              allowClear
              onChange={(value?: CodeLibelle) => {
                setStructureRequest(handleEss({ ...structureRequest, statutJuridique: value }))
              }}
            />
          </Descriptions.Item>
          <Descriptions.Item
            className={DESCRIPTION_ITEM_CLASSNAME}
            labelStyle={LABEL_STYLE}
            label={<Text strong>Statut coopératif</Text>}
          >
            <ReferenceSelect
              referenceCode={ReferenceCode.STATUT_COOPERATIF}
              code={structureRequest.statutCooperatif?.code}
              allowClear
              onChange={(value?: CodeLibelle) => {
                setStructureRequest(handleEss({ ...structureRequest, statutCooperatif: value }))
              }}
            />
          </Descriptions.Item>
          <Descriptions.Item
            className={DESCRIPTION_ITEM_CLASSNAME}
            labelStyle={LABEL_STYLE}
            label={<Text strong>Agrément ESUS</Text>}
          >
            <ReferenceSelect
              referenceCode={ReferenceCode.AGREMENT_ESUS}
              code={structureRequest.agrementEsus?.code}
              onChange={(value?: CodeLibelle) => {
                let newStructureRequest = { ...structureRequest, agrementEsus: value }

                if (structureRequest.agrementEsus?.code === "PAS_AGR") {
                  newStructureRequest = {
                    ...newStructureRequest,
                    dureeEsus: undefined,
                    dateDemandeEsus: undefined,
                    dateObtentionEsus: undefined
                  }
                }

                setStructureRequest(newStructureRequest)
              }}
            />
          </Descriptions.Item>
          {structureRequest.agrementEsus?.code !== undefined &&
          structureRequest.agrementEsus.code !== "PAS_AGR" ? (
            <>
              <Descriptions.Item
                className={DESCRIPTION_ITEM_CLASSNAME}
                labelStyle={LABEL_STYLE}
                label={<Text strong>Date de demande ESUS</Text>}
              >
                <AppMetierDateInput
                  value={
                    structureRequest.dateDemandeEsus
                      ? dayjs(structureRequest.dateDemandeEsus)
                      : undefined
                  }
                  onChange={value =>
                    setStructureRequest({
                      ...structureRequest,
                      dateDemandeEsus: value ? formatDayjs(value, SERVER_DATE_FORMAT) : undefined
                    })
                  }
                />
              </Descriptions.Item>
              <Descriptions.Item
                className={DESCRIPTION_ITEM_CLASSNAME}
                labelStyle={LABEL_STYLE}
                label={<Text strong>Date d'obtention ESUS</Text>}
              >
                <AppMetierDateInput
                  value={
                    structureRequest.dateObtentionEsus
                      ? dayjs(structureRequest.dateObtentionEsus)
                      : undefined
                  }
                  onChange={value =>
                    setStructureRequest({
                      ...structureRequest,
                      dateObtentionEsus: value ? formatDayjs(value, SERVER_DATE_FORMAT) : undefined
                    })
                  }
                />
              </Descriptions.Item>
              <Descriptions.Item
                className={DESCRIPTION_ITEM_CLASSNAME}
                labelStyle={LABEL_STYLE}
                label={<Text strong>Durée d'agrément (en mois)</Text>}
              >
                <NumberInputWithSteps
                  value={structureRequest.dureeEsus ?? 0}
                  suffix={Suffixes.MONTHS}
                  pluralSuffix={Suffixes.MONTHS}
                  onChange={value => {
                    setStructureRequest({ ...structureRequest, dureeEsus: value })
                  }}
                />
              </Descriptions.Item>
            </>
          ) : null}
          <Descriptions.Item labelStyle={LABEL_STYLE} label={<Text strong>ESS</Text>}>
            <AppMetierSwitch
              checked={structureRequest.ess ?? false}
              label=""
              onChange={() => {
                setStructureRequest({ ...structureRequest, ess: !structureRequest.ess })
              }}
              checkedInnerLabel="Oui"
              uncheckedInnerLabel="Non"
              disabled={isEssFixed(structureRequest)}
            />
          </Descriptions.Item>
        </Descriptions>
      </Space>
    </Card>
  )
}

export const FutureDateMessage = (style: React.CSSProperties) => (
  <Text type="danger" style={style}>
    La date ne peut pas être postérieure à la date du jour.
  </Text>
)
